import clsx from 'clsx';
import { Badge, Flex, Heading, Paragraph, Stack, Tooltip } from '@customerio/pluma-components/react';
import { useAnalytics } from '@/services/analytics';
import { IntegrationType } from '../../catalog/enums';
import { audienceCardClickProperties, standardCardClickProperties } from '../analytics';
import {
  buildLabels,
  featureGatedText,
  featureGatedTitle,
  isDescriptionTruncated,
  isFeatureGated,
  isPipelineCard,
  resolveCardHref,
  truncateDescription,
  type PremiumGates,
} from '../card-model';
import type { SynthesizedIntegration } from '../types';
import * as cardStyles from './card.css';
import { CardLabels } from './card-labels';
import { DirectoryCardLink } from './directory-card-link';
import { IntegrationIcon } from './integration-icon';

type DirectoryCardProps = {
  integration: SynthesizedIntegration;
  workspaceId: string;
  gates: PremiumGates;
  isTrial: boolean;
  /** Ad-audience cards fire a different (Advertising) click event. */
  isAudienceSync?: boolean;
  /** Extra class for grid placement (e.g. centering a lone Zapier result). */
  className?: string;
};

export function DirectoryCard({
  integration,
  workspaceId,
  gates,
  isTrial,
  isAudienceSync = false,
  className,
}: DirectoryCardProps) {
  const { track } = useAnalytics();
  const cardType = integration.id;
  const featureGated = isFeatureGated(integration, gates);
  const labels = buildLabels(integration, gates);
  const pipeline = isPipelineCard(cardType);
  const href = resolveCardHref(integration, cardType, workspaceId);
  const truncated = isDescriptionTruncated(integration.overview);
  const description = truncateDescription(integration.overview);

  const handleClick = () => {
    if (isAudienceSync) {
      void track('integration', 'clicked', audienceCardClickProperties(integration, integration.enabled));
      return;
    }
    if (integration.name === '') {
      return;
    }
    void track('integration', 'clicked', standardCardClickProperties(integration));
  };

  return (
    <DirectoryCardLink
      cardType={cardType}
      className={clsx(
        cardStyles.resetCard,
        cardStyles.cardMinHeight,
        featureGated && cardStyles.gatedFeatureCard,
        className,
      )}
      featureGated={featureGated}
      external={integration.external}
      isPipelineCard={pipeline}
      href={href}
      onClick={handleClick}
      gatedTitle={featureGatedTitle(integration.name, integration.dataFlow)}
      gatedText={featureGatedText(integration.name, integration.dataFlow, isTrial)}
      featureName={integration.name}
    >
      <Stack className={cardStyles.card} gap="200" alignBlock="space-between">
        <Flex direction="column" flexGrow="1">
          <IntegrationIcon
            integration={{ icon: integration.icon, imageSrc: integration.imageSrc, title: integration.name }}
          />
          <Heading level="4" color="active" mt="250" mb="100" data-test-title>
            {integration.name}
            {integration.type === IntegrationType.zapier ? (
              <Badge as="span" color="clementine">
                via Zapier
              </Badge>
            ) : null}
          </Heading>
          <Tooltip content={integration.overview} isDisabled={!truncated}>
            <Paragraph variant="secondary" data-test-description>
              {description}
            </Paragraph>
          </Tooltip>
        </Flex>
        <CardLabels labels={labels} />
      </Stack>
    </DirectoryCardLink>
  );
}
